import type { CompetitionStatus } from '@napaicp/shared'
import type { SubmissionStatus } from '@napaicp/schemas'

/**
 * PR-186/§30 (Student Journey Registration & Participation IA/UX Spec Delta,
 * `docs/incoming/NAPAICP_Student_Competition_Registration_Participation_Journey_IA_UX_Spec_SDD.md`):
 * the "Critical requirement" — one pure decision point for "what stage is
 * this student at, and what's next" — shared by every surface that used to
 * answer that question independently:
 *
 * - `student-competition-page.tsx`'s inline `nextStep` + `steps` (Workspace
 *   Progress/Next Action);
 * - `features/home/derive-home.ts`'s `deriveStudentAction` (Home "next
 *   actions" list);
 * - `features/public/participant-cta.ts`'s student branch (Public
 *   Competition Detail CTA).
 *
 * Unifying these was not just a refactor — reconciling the three exposed
 * real behavioral gaps, since `participant-cta.ts` already had the richest,
 * most correct model (submission-window-phase awareness, "missed the
 * window" vs "not open yet" vs "in progress", result-published
 * precedence per `BR-137`) while the other two ignored submission-window
 * timing and never considered a published result at all. Consumers now
 * inherit the more correct behavior instead of each carrying its own
 * partial, silently-diverging copy of "first unfinished step wins".
 *
 * This module is pure — no fetching, no JSX, no i18n — so every branch is
 * unit-testable without mounting React or mocking `fetch`. Each consumer
 * still owns its own presentation vocabulary (`ParticipantCtaKind`,
 * `HomeActionKind`, the Workspace's own next-step copy) and maps the
 * `StudentJourneyStage` this module returns onto that vocabulary — the
 * *priority/precedence logic itself* is the only thing centralized here.
 */

/** Where the submission window currently sits relative to now, derived from lifecycle status alone (§15.2/UX-167: "尚未到作品繳交時間" / "作品繳交已截止" are distinct blocked states, not one generic "not started"). */
export type SubmissionWindowPhase = 'before' | 'open' | 'after'

/** The submission window dates that decide {@link submissionWindowPhaseForStatus} when the status alone cannot (BR-117). */
export interface SubmissionWindowDates {
  submissionStartAt: string | null
  submissionEndAt: string | null
}

/**
 * Maps the same effective lifecycle status every surface already has
 * (`CompetitionDetailDto.status`, `PublicCompetitionStatus`,
 * `CompetitionScheduleDates.status` — all the same underlying
 * `CompetitionStatus` enum) to a submission-window phase. `judging`,
 * `completed`, and `archived` all mean the submission window has already
 * closed — this is the "after" bucket `participant-cta.ts` originally
 * grouped together to distinguish "you never got here" (`submission_missed`)
 * from "you're still waiting for it to open" (`submission_not_open`).
 */
export function submissionWindowPhaseForStatus(
  status: CompetitionStatus,
  submissionWindow?: SubmissionWindowDates,
  now: Date = new Date(),
): SubmissionWindowPhase {
  if (status === 'submission_open') return 'open'
  if (status === 'judging' || status === 'completed' || status === 'archived') return 'after'
  // BR-117: when the registration and submission windows overlap the status
  // is `registration_open`, yet the submission window is already open. Only
  // the explicit dates can tell (same both-dates-required rule as the
  // server's `isWithin`); without them keep the status-only answer.
  if (
    status === 'registration_open' &&
    submissionWindow?.submissionStartAt != null &&
    submissionWindow.submissionEndAt != null &&
    now.getTime() >= Date.parse(submissionWindow.submissionStartAt) &&
    now.getTime() <= Date.parse(submissionWindow.submissionEndAt)
  ) {
    return 'open'
  }
  return 'before'
}

/**
 * BR-137/AC-210: mirrors the server's own `resultPublished` computation for
 * the public `myStatus` DTO (`resolveMyStatus` in
 * `apps/api/src/routes/public-competitions.ts`) — every other authenticated
 * competition DTO (`CompetitionDetailDto`, `CompetitionSummaryDto`) has no
 * such boolean yet, so callers derive it client-side from the same two
 * fields the server uses. Presentation only: Server remains authoritative
 * for anything that actually gates a mutation (SEC-PROPOSED-033) — this
 * never unlocks a CTA that isn't already gated server-side, it only decides
 * what to show. A plain top-level function (not inline in a component body)
 * so the `Date.now()` read stays outside render — every caller must import
 * this rather than reimplementing the check inline.
 */
export function isResultPublished(
  status: CompetitionStatus,
  resultPublishAt: string | null,
): boolean {
  return (
    status === 'completed' && resultPublishAt !== null && Date.now() >= Date.parse(resultPublishAt)
  )
}

const LIVE_SUBMISSION_STATUSES = new Set<SubmissionStatus>(['draft', 'ready'])
const SUBMITTED_SUBMISSION_STATUSES = new Set<SubmissionStatus>(['submitted', 'locked'])

export interface StudentJourneyInput {
  /** Whether the competition has a published registration form at all — `false` means registration is trivially complete (nothing to fill in). */
  registrationRequired: boolean
  registrationCompleted: boolean
  /** Team-based competition (`teamMaxMembers > 1` / `participationType === 'team'`) — `false` means team status is never consulted. */
  teamRequired: boolean
  hasTeam: boolean
  /** The caller's (team's) most recent submission status, or `null` if none exists yet. `withdrawn`/`disqualified` are treated the same as `null` — the caller no longer has a live or submitted entry. */
  submissionStatus: SubmissionStatus | null
  /**
   * PR-258/BR-191/UX-253 (Student Journey Completion Wave SJ-3): `true`
   * when this submission has a non-null `submittedAt` — i.e. it was
   * formally submitted at least once before landing back in a live
   * (`draft`/`ready`) status. `submittedAt` is only ever set by the actual
   * formal-submit commit and is never cleared by `/unlock` or
   * `/return-for-correction` (both just move `status` back to `ready`), so
   * this is an authoritative signal already on `SubmissionDto` — not a
   * guess. The API does not yet expose *why* it was reopened (no
   * student-visible correction-reason field exists — see the incoming
   * Student Journey spec §11.1's own SPEC-GAP note), so this only drives
   * the generic "a submission you already sent in is back in your hands"
   * presentation, never a fabricated reason.
   */
  wasSubmittedBefore: boolean
  submissionWindowPhase: SubmissionWindowPhase
  /** Effective lifecycle status is `completed` — distinguishes "已送出，等待評審" from "競賽已結束" once a submission exists but no result has published yet. */
  competitionCompleted: boolean
  /** BR-137/AC-210: outranks every other stage the moment it's true. */
  resultPublished: boolean
  /**
   * PR-260/PR-261/UX-257 (round-aware Student Result Journey, Wave SJ-5):
   * `true` when every other submission-related field above describes the
   * *final* round (the caller has already advanced there), not the
   * preliminary round. Only relabels the four submission-in-progress
   * stages into their `final_*` counterparts (see `StudentJourneyStage`
   * and §14.1 of the incoming spec) — never changes precedence, and never
   * introduces a second decision engine (BR-197: final round reuses the
   * exact same Submission Workbench). Callers that do not yet have
   * unambiguous round context (e.g. a flattened "most recent submission"
   * read with no round id attached) should pass `false` rather than
   * guess — the ordinary preliminary-round labels are still correct, just
   * not final-round-specific.
   */
  isFinalRound: boolean
}

export type StudentJourneyStage =
  | 'registration'
  | 'team'
  | 'submission_not_open'
  | 'submission_start'
  | 'submission_continue'
  /**
   * UX-168: a live entry marked `ready` (as opposed to still `draft`) is far
   * enough along that "繼續準備作品" undersells where the student actually
   * is — this stage exists so every consumer can offer a distinct "檢查並
   * 送出作品" CTA instead of reusing `submission_continue`'s copy for both.
   */
  | 'submission_ready'
  | 'submission_missed'
  /**
   * BR-206/AC-318 (Student Participant UI/UX/IA v4.45, Wave E bug fix): a
   * live (`draft`/`ready`) entry that was *never* formally submitted
   * (`wasSubmittedBefore === false`) once the submission window has closed
   * (including the competition's `judging` phase). Before this stage
   * existed, this case collapsed into `awaiting_judging` — the same stage a
   * genuinely submitted-and-awaiting-review entry gets, which the shared
   * stepper (`deriveJourneyProgressSteps`) renders as "done" (✓) for the
   * submission step. That is exactly the state confusion BR-206 forbids:
   * "競賽目前已進入評審階段" is a true statement about the competition, but
   * "我的作品仍是草稿" must never be presented as `✓ 作品繳交`. This stage is
   * the distinct "nothing left to do, but never actually sent it in" case —
   * closer to `submission_missed` (window closed, nothing submitted) than to
   * `awaiting_judging` (something real is with the judges).
   */
  | 'submission_expired'
  /**
   * PR-258/BR-191/UX-253: a live (`draft`/`ready`) entry that has already
   * been formally submitted before (`wasSubmittedBefore`) — staff reopened
   * it via `/unlock` or `/return-for-correction` — outranks the ordinary
   * `submission_continue`/`submission_ready` presentation so the student
   * sees "this needs your attention again", not "still working on your
   * first draft".
   */
  | 'correction_required'
  | 'awaiting_judging'
  /**
   * UX-168: a `locked` submission (organizer/staff has locked it — no
   * longer editable even via `returnForCorrection`, distinct from the
   * ordinary `submitted` state) is further along than a merely-submitted
   * entry, so it gets its own "查看已提交作品" CTA instead of reusing
   * `awaiting_judging`'s "查看投稿收據" copy. Parallels `awaiting_judging`
   * for stepper/progress purposes (judging step is still `current`).
   */
  | 'submission_locked'
  | 'competition_done'
  /** UX-168: `submission_locked`'s counterpart once the competition itself has concluded — parallels `competition_done` for stepper purposes (results step is `current`). */
  | 'submission_locked_done'
  | 'result_published'
  /**
   * PR-260/PR-261/UX-257 (round-aware Student Result Journey, Wave SJ-5):
   * presentation-only relabels of `submission_start`/`submission_continue`/
   * `submission_ready`/`awaiting_judging`, applied by
   * `deriveStudentJourneyStage` itself when `isFinalRound` is true — never
   * a second decision engine, per BR-197 (final round reuses the exact
   * same Submission Workbench as the preliminary round).
   */
  | 'final_submission_start'
  | 'final_submission_continue'
  | 'final_submission_ready'
  | 'final_awaiting_judging'
  /**
   * Session 7 (this delivery, item 3): `submission_expired`'s own
   * final-round counterpart, mirroring the other three
   * submission-in-progress stages above. Before this fix,
   * `FINAL_ROUND_STAGE_RELABEL` had no entry for `submission_expired`, so a
   * final-round caller whose entry was never formally submitted (BR-206)
   * fell through `deriveStudentJourneyStage`'s relabel step unchanged and
   * every downstream consumer showed the *preliminary*-round wording
   * (`expiredTask`: "作品繳交已截止") instead of a final-round-specific one —
   * not wrong about the fact, but inconsistent with every sibling stage,
   * which all get a distinct final-round label per section 14.1 of the
   * incoming spec. Same stepper treatment as `submission_expired` (never
   * "done").
   */
  | 'final_submission_expired'

const FINAL_ROUND_STAGE_RELABEL: Partial<Record<StudentJourneyStage, StudentJourneyStage>> = {
  submission_start: 'final_submission_start',
  submission_continue: 'final_submission_continue',
  submission_ready: 'final_submission_ready',
  awaiting_judging: 'final_awaiting_judging',
  submission_expired: 'final_submission_expired',
}

/**
 * The one "first unfinished step wins" priority rule, in order:
 * result published (§15.1/BR-137, always wins) → registration →
 * team → submission (start/continue/blocked-by-window) → judging/done.
 * `isFinalRound` is applied last, as a pure relabel — see
 * `FINAL_ROUND_STAGE_RELABEL` — so it can never change this precedence.
 */
export function deriveStudentJourneyStage(input: StudentJourneyInput): StudentJourneyStage {
  const stage = deriveBaseStudentJourneyStage(input)
  if (!input.isFinalRound) return stage
  return FINAL_ROUND_STAGE_RELABEL[stage] ?? stage
}

function deriveBaseStudentJourneyStage(input: StudentJourneyInput): StudentJourneyStage {
  if (input.resultPublished) return 'result_published'
  if (input.registrationRequired && !input.registrationCompleted) return 'registration'
  if (input.teamRequired && !input.hasTeam) return 'team'

  const status = input.submissionStatus
  const isSubmitted = status !== null && SUBMITTED_SUBMISSION_STATUSES.has(status)
  const hasLiveEntry = status !== null && LIVE_SUBMISSION_STATUSES.has(status)

  if (isSubmitted) {
    // UX-168: `locked` (organizer/staff-locked, no longer returnable to the
    // student) is distinct from an ordinary `submitted` entry — same
    // completed/not-completed split, different CTA copy per state.
    const locked = status === 'locked'
    if (input.competitionCompleted) return locked ? 'submission_locked_done' : 'competition_done'
    return locked ? 'submission_locked' : 'awaiting_judging'
  }
  if (hasLiveEntry) {
    // PR-258/BR-191: outranks the window-closed/ready/continue split below —
    // a reopened submission needs attention regardless of window phase or
    // its current draft/ready sub-state.
    if (input.wasSubmittedBefore) return 'correction_required'
    // BR-206/AC-318: a draft/ready entry that was never formally submitted,
    // once the window has closed, is `submission_expired` — distinct from
    // `awaiting_judging` (see that stage's own doc comment for why this
    // split exists and what it fixes).
    if (input.submissionWindowPhase !== 'open') return 'submission_expired'
    // UX-168: distinguish "still drafting" from "marked ready, needs a
    // final check before formal submit" — status is guaranteed 'draft' or
    // 'ready' here (`LIVE_SUBMISSION_STATUSES`).
    return status === 'ready' ? 'submission_ready' : 'submission_continue'
  }
  // No live or submitted entry (including `withdrawn`/`disqualified`/`null`).
  switch (input.submissionWindowPhase) {
    case 'before':
      return 'submission_not_open'
    case 'open':
      return 'submission_start'
    case 'after':
      return 'submission_missed'
  }
}

// ---------------------------------------------------------------------------
// Progress stepper (✓ / ● / ○) — UX-082/UX-144–147
// ---------------------------------------------------------------------------

export type ProgressStepState = 'done' | 'current' | 'upcoming'
export type JourneyProgressStepKey = 'registration' | 'team' | 'submission' | 'judging' | 'results'

export interface JourneyProgressStep {
  key: JourneyProgressStepKey
  state: ProgressStepState
}

const BEFORE_SUBMISSION_STAGES = new Set<StudentJourneyStage>(['registration', 'team'])
const AT_SUBMISSION_STAGES = new Set<StudentJourneyStage>([
  'submission_not_open',
  'submission_start',
  'submission_continue',
  'submission_ready',
  'submission_missed',
  // BR-206/AC-318: never actually submitted, so the submission step must
  // never render as "done" (✓) just because the window closed or the
  // competition moved on to judging.
  'submission_expired',
  // PR-258/BR-191: a reopened submission is back at the submission step
  // (current), not already through judging (done) — the stepper must
  // never show "judging" as finished while the student still has an
  // authorized correction to make.
  'correction_required',
  // PR-260/PR-261: the final round's submission-in-progress stages are
  // presentation-only relabels of the ones above (see
  // `FINAL_ROUND_STAGE_RELABEL`) — same stepper treatment.
  'final_submission_start',
  'final_submission_continue',
  'final_submission_ready',
  // Session 7 (item 3): `submission_expired`'s final-round counterpart —
  // never actually submitted, so it must never render as "done" either,
  // same reasoning as `submission_expired` above.
  'final_submission_expired',
])

/**
 * Derives the same 5-step (4 for an individual competition) horizontal
 * stepper from the one `StudentJourneyStage` value, instead of the
 * Workspace page independently re-deriving each step's state from its own
 * local booleans (which is how `results` used to go `current` purely off
 * `competition.status === 'completed'`, disagreeing with a `nextStep` that
 * could still say `registration` — an internal contradiction `BR-135`/
 * `AC-205` explicitly forbids).
 */
export function deriveJourneyProgressSteps(
  stage: StudentJourneyStage,
  teamRequired: boolean,
): JourneyProgressStep[] {
  const steps: JourneyProgressStep[] = [
    { key: 'registration', state: stage === 'registration' ? 'current' : 'done' },
  ]

  if (teamRequired) {
    steps.push({
      key: 'team',
      state: stage === 'registration' ? 'upcoming' : stage === 'team' ? 'current' : 'done',
    })
  }

  const beforeSubmission = BEFORE_SUBMISSION_STAGES.has(stage)
  const atSubmission = AT_SUBMISSION_STAGES.has(stage)
  steps.push({
    key: 'submission',
    state: beforeSubmission ? 'upcoming' : atSubmission ? 'current' : 'done',
  })

  steps.push({
    key: 'judging',
    state:
      beforeSubmission || atSubmission
        ? 'upcoming'
        : stage === 'awaiting_judging' ||
            stage === 'submission_locked' ||
            stage === 'final_awaiting_judging'
          ? 'current'
          : 'done',
  })

  steps.push({
    key: 'results',
    state:
      stage === 'competition_done' ||
      stage === 'submission_locked_done' ||
      stage === 'result_published'
        ? 'current'
        : 'upcoming',
  })

  return steps
}
